// Customer context selector for issue recording
import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCustomers, useCustomerEntities, useCustomerUsers } from '@/hooks/useCustomers'
import { Building2 } from 'lucide-react'

interface CustomerContextSelectorProps {
  customerId: string | null
  entityId: string | null
  userId: string | null
  onCustomerChange: (id: string | null) => void
  onEntityChange: (id: string | null) => void
  onUserChange: (id: string | null) => void
}

export function CustomerContextSelector({
  customerId,
  entityId,
  userId,
  onCustomerChange,
  onEntityChange,
  onUserChange,
}: CustomerContextSelectorProps) {
  const { customers, isLoading: customersLoading } = useCustomers()
  const { entities, isLoading: entitiesLoading } = useCustomerEntities(customerId)
  const { users, isLoading: usersLoading } = useCustomerUsers(customerId)

  const [localCustomerId, setLocalCustomerId] = useState<string>(customerId || '')
  const [localEntityId, setLocalEntityId] = useState<string>(entityId || '_none')
  const [localUserId, setLocalUserId] = useState<string>(userId || '_none')

  useEffect(() => {
    setLocalCustomerId(customerId || '')
  }, [customerId])

  useEffect(() => {
    setLocalEntityId(entityId || '_none')
  }, [entityId])

  useEffect(() => {
    setLocalUserId(userId || '_none')
  }, [userId])

  const handleCustomerChange = (value: string) => {
    setLocalCustomerId(value)
    setLocalEntityId('_none')
    setLocalUserId('_none')
    onCustomerChange(value || null)
    onEntityChange(null)
    onUserChange(null)
  }

  const handleEntityChange = (value: string) => {
    const entityId = value === '_none' ? '' : value
    setLocalEntityId(entityId)
    onEntityChange(entityId || null)
  }

  const handleUserChange = (value: string) => {
    const uid = value === '_none' ? '' : value
    setLocalUserId(uid)
    onUserChange(uid || null)
  }

  const activeCustomers = customers.filter((c) => c.is_active)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="h-4 w-4" />
          Customer Context
        </CardTitle>
        <CardDescription className="text-xs">
          Select customer, entity, and user for this issue
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Customer Selection */}
        <div className="space-y-1.5">
          <Label htmlFor="customer" className="text-xs">
            Customer <span className="text-red-500">*</span>
          </Label>
          <Select
            value={localCustomerId}
            onValueChange={handleCustomerChange}
            disabled={customersLoading}
          >
            <SelectTrigger id="customer">
              <SelectValue placeholder={customersLoading ? 'Loading...' : 'Select customer'} />
            </SelectTrigger>
            <SelectContent>
              {activeCustomers.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                  {customer.company_code && ` (${customer.company_code})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Entity Selection */}
        {localCustomerId && (
          <div className="space-y-1.5">
            <Label htmlFor="entity" className="text-xs">
              D365 Entity
            </Label>
            <Select
              value={localEntityId}
              onValueChange={handleEntityChange}
              disabled={entitiesLoading || entities.length === 0}
            >
              <SelectTrigger id="entity">
                <SelectValue
                  placeholder={
                    entitiesLoading
                      ? 'Loading...'
                      : entities.length === 0
                        ? 'No entities available'
                        : 'Select entity (optional)'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {entities.map((entity) => (
                  <SelectItem key={entity.id} value={entity.id}>
                    {entity.name}
                    {entity.d365_version && ` (v${entity.d365_version})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* User Selection */}
        {localCustomerId && (
          <div className="space-y-1.5">
            <Label htmlFor="user" className="text-xs">
              Reported By
            </Label>
            <Select
              value={localUserId}
              onValueChange={handleUserChange}
              disabled={usersLoading || users.length === 0}
            >
              <SelectTrigger id="user">
                <SelectValue
                  placeholder={
                    usersLoading
                      ? 'Loading...'
                      : users.length === 0
                        ? 'No users available'
                        : 'Select user (optional)'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                    {user.email && ` (${user.email})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {activeCustomers.length === 0 && !customersLoading && (
          <p className="text-xs text-muted-foreground">
            No customers configured. Go to Settings → Customers to add customers.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
